import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Avatar
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../api';

function ResearchVault() {
  const [selectedCases, setSelectedCases] = useState([]); // Changed to array
  const [cases, setCases] = useState([]);
  const [allCaseDocuments, setAllCaseDocuments] = useState({}); // Documents grouped by case
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  const chatEndRef = useRef(null);
  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (selectedCases.length > 0) {
      fetchMultipleCaseDocuments();
      // Optionally fetch research history for the first case or all cases
    } else {
      setAllCaseDocuments({});
      setChatHistory([]);
      setSelectedDocuments([]);
    }
  }, [selectedCases]);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const res = await apiService.getCases();
      setCases(res.data.cases || []);
      setError('');
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases');
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMultipleCaseDocuments = async () => {
    try {
      const documentsMap = {};
      
      // Fetch documents for each selected case
      for (const caseId of selectedCases) {
        try {
          const res = await apiService.getCaseDocuments(caseId);
          const docs = res.data.documents || [];
          documentsMap[caseId] = docs;
        } catch (err) {
          console.error(`Error fetching documents for case ${caseId}:`, err);
          documentsMap[caseId] = [];
        }
      }
      
      setAllCaseDocuments(documentsMap);
      // Don't auto-select documents - let user choose
      setSelectedDocuments([]);
    } catch (err) {
      console.error('Error fetching case documents:', err);
      setError('Failed to load case documents');
      setAllCaseDocuments({});
      setSelectedDocuments([]);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (selectedCases.length === 0) {
      setError('Please select at least one case first');
      return;
    }

    // Upload to the first selected case
    const targetCaseId = selectedCases[0];
    setUploading(true);
    setError('');

    try {
      for (const file of acceptedFiles) {
        await apiService.uploadDocument(targetCaseId, file);
      }
      
      fetchMultipleCaseDocuments();
    } catch (err) {
      console.error('Error uploading files:', err);
      setError('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    }
  });

  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || selectedCases.length === 0 || selectedDocuments.length === 0) return;

    const userMessage = chatMessage.trim();
    setChatMessage('');
    setChatLoading(true);

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };

    setChatHistory(prev => [...prev, newUserMessage]);

    try {
      const res = await apiService.sendResearchChat({
        case_ids: selectedCases, // Send all selected case IDs
        message: userMessage,
        document_ids: selectedDocuments
      });

      // Add AI response to chat
      const aiMessage = {
        role: 'assistant',
        content: res.data.response,
        document_refs: res.data.document_refs || [],
        timestamp: new Date().toISOString()
      };

      setChatHistory(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('Error sending research message:', err);
      setError('Failed to send message');
      
      // Add error message
      setChatHistory(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Research Vault
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-powered research assistant - Select multiple cases to analyze documents across cases
          </Typography>
        </Box>
        <Autocomplete
          multiple
          options={cases}
          getOptionLabel={(option) => option.title}
          value={cases.filter(c => selectedCases.includes(c.id))}
          onChange={(event, newValue) => {
            setSelectedCases(newValue.map(c => c.id));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Cases"
              placeholder="Choose one or more cases"
              size="small"
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.title}
            </li>
          )}
          sx={{ minWidth: 400 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {selectedCases.length === 0 ? (
        <Card sx={{ p: 8, textAlign: 'center', boxShadow: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box>
            <FolderIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Select one or more cases to start researching
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose cases from the dropdown above to view documents and chat with AI
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
              💡 Tip: Select multiple cases to analyze documents across different cases
            </Typography>
          </Box>
        </Card>
      ) : (
        <Grid container spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Sidebar - Document Collections */}
          <Grid item xs={12} md={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Card sx={{ boxShadow: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon fontSize="small" color="primary" />
                    Documents ({Object.values(allCaseDocuments).flat().length})
                  </Typography>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                    {selectedDocuments.length} selected
                  </Typography>
                </Box>

                {/* Upload Area */}
                <Paper
                  {...getRootProps()}
                  sx={{
                    p: 1.5,
                    mb: 2,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }
                  }}
                >
                  <input {...getInputProps()} />
                  <UploadFileIcon color="primary" sx={{ fontSize: 28, mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    {uploading ? 'Uploading...' : isDragActive ? 'Drop here' : 'Upload to first case'}
                  </Typography>
                </Paper>

                <Divider sx={{ mb: 2, flexShrink: 0 }} />

                {/* Document List - Grouped by Case */}
                <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                  {Object.keys(allCaseDocuments).length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No documents yet
                      </Typography>
                    </Box>
                  ) : (
                    selectedCases.map((caseId) => {
                      const caseData = cases.find(c => c.id === caseId);
                      const docs = allCaseDocuments[caseId] || [];
                      
                      return (
                        <Box key={caseId} sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 600, color: 'primary.main', bgcolor: 'primary.50', borderRadius: 1 }}>
                            {caseData?.title || 'Unknown Case'} ({docs.length})
                          </Typography>
                          <List dense sx={{ pt: 0.5 }}>
                            {docs.length === 0 ? (
                              <ListItem>
                                <ListItemText 
                                  primary="No documents" 
                                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                />
                              </ListItem>
                            ) : (
                              docs.map((doc) => (
                                <ListItem
                                  key={doc.id}
                                  sx={{
                                    borderRadius: 1,
                                    py: 0.5,
                                    px: 1,
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedDocuments.includes(doc.id)}
                                    onChange={() => toggleDocumentSelection(doc.id)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                  />
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <DescriptionIcon 
                                      fontSize="small"
                                      color={selectedDocuments.includes(doc.id) ? 'primary' : 'action'} 
                                    />
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={doc.filename}
                                    primaryTypographyProps={{ 
                                      variant: 'body2',
                                      noWrap: true,
                                      sx: { fontWeight: selectedDocuments.includes(doc.id) ? 600 : 400 }
                                    }}
                                  />
                                </ListItem>
                              ))
                            )}
                          </List>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Divider sx={{ my: 2, flexShrink: 0 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                  <AttachFileIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {selectedDocuments.length} of {Object.values(allCaseDocuments).flat().length} documents selected
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  Check documents to include in research
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Main Content - Chat Interface */}
          <Grid item xs={12} md={9} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Card sx={{ boxShadow: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Chat Header */}
              <CardContent sx={{ borderBottom: 1, borderColor: 'divider', py: 2, flexShrink: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyIcon color="primary" />
                  Research Assistant
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ask questions about your case documents using AI
                </Typography>
              </CardContent>

              {/* Chat Messages */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: 'grey.50', minHeight: 0 }}>
                {chatHistory.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <SmartToyIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Start Your Research
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ask questions about your case documents using natural language
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Select documents from the sidebar to include in the research context
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {chatHistory.map((message, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mb: 3,
                          alignItems: 'flex-start'
                        }}
                      >
                        <Avatar sx={{ bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main' }}>
                          {message.role === 'user' ? <PersonIcon /> : <SmartToyIcon />}
                        </Avatar>
                        <Paper sx={{ flex: 1, p: 2 }}>
                          <Box sx={{ 
                            whiteSpace: 'pre-wrap',
                            '& p': { 
                              margin: '0.5em 0',
                              lineHeight: 1.6
                            },
                            '& p:first-of-type': {
                              marginTop: 0
                            },
                            '& p:last-of-type': {
                              marginBottom: 0
                            },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              margin: '1em 0 0.5em 0',
                              lineHeight: 1.3,
                              fontWeight: 600
                            },
                            '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': {
                              marginTop: 0
                            },
                            '& ul, & ol': {
                              margin: '0.5em 0',
                              paddingLeft: '1.5em'
                            },
                            '& li': {
                              margin: '0.25em 0',
                              lineHeight: 1.5
                            },
                            '& blockquote': {
                              margin: '0.5em 0',
                              paddingLeft: '1em',
                              borderLeft: '3px solid #ddd'
                            },
                            '& code': {
                              backgroundColor: '#f5f5f5',
                              padding: '0.1em 0.3em',
                              borderRadius: '3px',
                              fontSize: '0.9em'
                            },
                            '& pre': {
                              margin: '0.5em 0',
                              padding: '1em',
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              overflow: 'auto'
                            },
                            '& hr': {
                              margin: '1em 0',
                              border: 'none',
                              borderTop: '1px solid #ddd'
                            }
                          }}>
                            {message.role === 'assistant' ? (
                              <ReactMarkdown>{message.content}</ReactMarkdown>
                            ) : (
                              <Typography variant="body2">{message.content}</Typography>
                            )}
                          </Box>
                          {message.document_refs && message.document_refs.length > 0 && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {message.document_refs.map((ref, idx) => (
                                <Chip
                                  key={idx}
                                  label={ref}
                                  size="small"
                                  icon={<DescriptionIcon />}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {new Date(message.timestamp).toLocaleString()}
                          </Typography>
                        </Paper>
                      </Box>
                    ))}
                    <div ref={chatEndRef} />
                  </>
                )}
              </Box>

              {/* Chat Input */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder="Ask a question about your case documents..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={chatLoading || selectedDocuments.length === 0}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    endIcon={chatLoading ? <CircularProgress size={20} /> : <SendIcon />}
                    onClick={handleSendMessage}
                    disabled={!chatMessage.trim() || chatLoading || selectedDocuments.length === 0}
                    sx={{ minWidth: 100 }}
                  >
                    {chatLoading ? 'Thinking...' : 'Send'}
                  </Button>
                </Box>
                {selectedDocuments.length === 0 && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    Please select at least one document to start researching
                  </Typography>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      )}



    </Box>
  );
}

export default ResearchVault;